import { AfterViewInit, Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ValidationHandlerService, TranslatedToastrService, EditComponent, BaseComponent, LanguageService, DataTableRequest, OneColumnSearchData, ImgWithSrcComponentFixWidth75px, OrdererComponent } from 'lib/Vestra.shared';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService, CouponFilterType, CouponModel, CouponSortingModel, VestraService } from 'src/app/services/api-services';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ADTColumns, ADTSettings } from 'angular-datatables/src/models/settings';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
    selector: 'app-filter-coupon-list',
    templateUrl: './filter-coupon-list.component.html',
    styleUrls: ['./filter-coupon-list.component.scss'],
    standalone: false,
})
export class AllCouponListComponent extends BaseComponent implements OnInit, AfterViewInit {
    @ViewChild('actionsRef') actionsRef: TemplateRef<EditComponent>;
    @ViewChild('imgWidth75pxRef') imgWidth75pxRef: TemplateRef<ImgWithSrcComponentFixWidth75px>;
    @ViewChild('weeklyTopOrdererRef') weeklyTopOrdererRef: TemplateRef<OrdererComponent>;
    @ViewChild('carouselOrdererRef') carouselOrdererRef: TemplateRef<OrdererComponent>;

    @Input() filter: CouponFilterType;
    public coupons: CouponModel[] = [];

    public get showSaveSorting(): boolean {
        return this.filter !== CouponFilterType.All;
    }

    public get isCarouselTab(): boolean {
        return this.filter === CouponFilterType.Carousel;
    }

    public get isWeeklyTopTab(): boolean {
        return this.filter === CouponFilterType.WeeklyTop;
    }

    constructor(
        protected override validationHandlerService: ValidationHandlerService,
        private translatedToastrService: TranslatedToastrService,
        private translateService: TranslateService,
        protected override modalService: BsModalService,
        private vestraService: VestraService,
        private authenticationService: AuthenticationService,
        protected override spinnerService: NgxSpinnerService,
        protected override languageService: LanguageService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        super(spinnerService, modalService, validationHandlerService, translateService, languageService);
    }

    public ngOnInit(): void {
        this.dataTableOnInit();
    }

    public ngAfterViewInit() {
        this.dataTableAfterViewInit();
    }

    public ngOnDestroy() {
        this.dataTableOnDestroy();
    }

    public override getDtOptions() {
        this.languageUrl = this.languageService.getTranslationUrl();
        const columns = this.getColumns();

        const res: ADTSettings = {
            serverSide: true,
            processing: false,
            pageLength: -1,
            paging: false,
            order: [[1, 'asc']],
            lengthChange: false,
            responsive: {
                details: {
                    type: 'column',
                    target: -1,
                },
            },
            searching: false,
            destroy: true,
            language: {
                url: this.languageUrl,
            },
            ajax: (dataTablesParams: any, callback: any) => {
                const searchData: OneColumnSearchData = {
                    propertyValue: this.searchValue,
                };

                for (let order of dataTablesParams.order) {
                    order.name = dataTablesParams.columns[order.column]?.data;
                }

                const model: DataTableRequest = {
                    draw: dataTablesParams.draw,
                    start: dataTablesParams.start,
                    length: dataTablesParams.length,
                    order: dataTablesParams.order,
                    search: searchData,
                    columns: this.transformToColumnData(this.dtOptions.columns),
                };

                this.datatableRequest = model;

                this.handleSpinner(firstValueFrom(this.vestraService.getCouponListDataSource(this.filter, model)))
                    .then(res => {
                        this.coupons = res.data;
                        callback(this.getTableData(res));
                        return Promise.resolve(res);
                    })
                    .catch((err: HttpErrorResponse) => {
                        this.validationHandlerService.handleServerError(err);
                        callback(this.getEmptyTableData());
                    });
            },
            columns: columns,
            columnDefs: [
                {
                    targets: '_all',
                    visible: true,
                },
                {
                    className: 'details-icon dtr-control',
                    orderable: false,
                    target: -1,
                },
            ],
        };

        return res;
    }

    public onSearch(event: string): void {
        this.searchValue = event;
        this.refreshDataTable();
    }

    public onEditItem(event: any) {
        this.navigateToCouponEditPage(event.data.id, event.data.partnerId);
    }

    public onSaveSorting(): void {
        if (this.filter === CouponFilterType.WeeklyTop) {
            this.saveWeeklyTopSorting();
        } else if (this.filter === CouponFilterType.Carousel) {
            this.saveCarouselSorting();
        }
    }

    public navigateToCouponEditPage(id: number, partnerId: number) {
        this.router.navigate(['/administration/coupon-edit', partnerId, id]);
    }

    private saveWeeklyTopSorting(): void {
        let saveList: CouponSortingModel[] = [];
        for (let item of this.coupons) {
            let model: CouponSortingModel = {
                id: item.id,
                order: item.weeklyTopOrder,
            };

            saveList.push(model);
        }

        this.handleSpinner(
            firstValueFrom(this.vestraService.saveWeeklyTopCouponSorting(saveList))
                .then(res => {
                    this.refreshDataTable();
                    return Promise.resolve(res);
                })
                .catch((err: HttpErrorResponse) => {
                    this.validationHandlerService.handleServerError(err);
                })
        );
    }

    private saveCarouselSorting(): void {
        let saveList: CouponSortingModel[] = [];
        for (let item of this.coupons) {
            let model: CouponSortingModel = {
                id: item.id,
                order: item.carouselOrder,
            };

            saveList.push(model);
        }

        this.handleSpinner(
            firstValueFrom(this.vestraService.saveCarouselCouponSorting(saveList))
                .then(res => {
                    this.refreshDataTable();
                    return Promise.resolve(res);
                })
                .catch((err: HttpErrorResponse) => {
                    this.validationHandlerService.handleServerError(err);
                })
        );
    }

    private getColumns(): ADTColumns[] {
        const columns: ADTColumns[] = [
            {
                data: 'id',
                visible: false,
            },
        ];

        if (this.isWeeklyTopTab) {
            columns.push({
                title: 'common.sort_order',
                data: 'weeklyTopOrder',
                defaultContent: '',
                ngTemplateRef: {
                    ref: this.weeklyTopOrdererRef,
                },
                responsivePriority: 2,
            });
        }

        if (this.isCarouselTab) {
            columns.push({
                title: 'common.sort_order',
                data: 'carouselOrder',
                defaultContent: '',
                ngTemplateRef: {
                    ref: this.carouselOrdererRef,
                },
                responsivePriority: 2,
            });
        }

        const self = this;
        columns.push(
            {
                data: 'couponCode',
                title: 'coupon.code',
                searchable: false,
                orderable: true,
            },
            {
                data: 'redeemType',
                title: 'coupon.redeem_type',
                searchable: false,
                orderable: true,
                render: function (data, type, row, meta) {
                    let translateString = "coupon_redeem_type.not_set";
                    if(data)
                    {
                        if(data == 0)
                        {
                            translateString =  "coupon_redeem_type.not_set";
                        }
                        else if(data == 20)
                        {
                            translateString =  "coupon_redeem_type.online";
                        }
                        else if(data == 40)
                        {
                            translateString =  "coupon_redeem_type.qr_code";
                        }
                        else if(data == 60)
                        {
                            translateString =  "coupon_redeem_type.image_or_gif";
                        }
                    }
                    return self.translateService.instant(translateString)
                },
            },
            {
                data: 'redirectUrl',
                title: 'coupon.redirect_url',
                searchable: false,
                orderable: true,
                render: function (data, type, row, meta) {
                    if(data)
                    {
                        return self.translateService.instant("common.yes")
                    }
                    return self.translateService.instant("common.no")
                },
            },
            {
                data: 'title',
                title: 'coupon.title',
                searchable: false,
                orderable: true,
            },
            {
                data: 'description',
                title: 'coupon.description',
                searchable: false,
                orderable: true,
            },
            {
                data: 'price',
                title: 'coupon.price',
                searchable: false,
                orderable: true,
            },
            {
                data: 'discountPrice',
                title: 'coupon.discount_price',
                searchable: false,
                orderable: true,
                render: function (data, type, row, meta) {
                    if (data === null) {
                        return '';
                    }
                    return data;
                },
            },
            {
                data: 'discountPercentage',
                title: 'coupon.discount_percentage',
                searchable: false,
                orderable: true,
                render: function (data, type, row, meta) {
                    if (data === null) {
                        return '';
                    }
                    return data + '%';
                },
            },
            {
                data: 'expiryDate',
                title: 'coupon.expired_date',
                searchable: false,
                orderable: true,
                render: (data: string) => {
                    if (!data) return '';
                    const date = new Date(data);
                    return date.toLocaleDateString();
                },
            },
            {
                data: 'couponImageUrl',
                title: 'coupon.image',
                responsivePriority: 1,
                searchable: false,
                orderable: false,
                ngTemplateRef: {
                    ref: this.imgWidth75pxRef,
                },
            },
            {
                data: 'isActive',
                title: 'common.status',
                responsivePriority: 2,
                searchable: false,
                orderable: true,
                render: function (data, type, row, meta) {
                    return data ? '<img class="action-icon" src="../../../../assets/icon/check-mark.png"/>' : '<img class="action-icon" src="../../../../assets/icon/cross.png"/>';
                },
            },
            {
                data: 'isTopWeekly',
                title: 'coupon.is_top_weekly',
                responsivePriority: 2,
                searchable: false,
                orderable: true,
                visible: this.filter !== CouponFilterType.Carousel,
                render: function (data, type, row, meta) {
                    return data ? '<img class="action-icon" src="../../../../assets/icon/check-mark.png"/>' : '<img class="action-icon" src="../../../../assets/icon/cross.png"/>';
                },
            },
            {
                data: 'isCarousel',
                title: 'coupon.is_carousel',
                responsivePriority: 2,
                searchable: false,
                orderable: true,
                visible: this.filter !== CouponFilterType.WeeklyTop,
                render: function (data, type, row, meta) {
                    return data ? '<img class="action-icon" src="../../../../assets/icon/check-mark.png"/>' : '<img class="action-icon" src="../../../../assets/icon/cross.png"/>';
                },
            },
            {
                title: 'common.actions',
                data: null,
                searchable: false,
                orderable: false,
                defaultContent: '',
                ngTemplateRef: {
                    ref: this.actionsRef,
                    context: {
                        captureEvents: this.onEditItem.bind(this),
                    },
                },
                responsivePriority: 1,
            },
            {
                data: '',
                orderable: false,
                responsivePriority: 1,
                render: function (data) {
                    return '';
                },
            }
        );

        return columns;
    }
}
